/**
 * Navigation model (Beta-1 plan P6-07 / P6-08 / P3-12, Tim's D10 sidebars PDF + ⚑Q-09/Q-18/Q-26).
 *
 * One pure description of both sidebars, consumed by `components/Layout/Sidebar.jsx` (and by
 * unit tests). Two sidebars, chosen by the current path:
 *   - REPSit (platform, §1c): Dashboard · ACCESS LEVELS · REPSit LEARNING · INTELLIGENT
 *     ASSISTANTS (the ten IAs, inactive ones greyed) · ADMIN
 *   - DREW (the IA's app): OE Dashboard · ACCESS LEVELS · OE INPUT/ASSESSMENT ·
 *     DATA REVIEW/COMPARISON · MAIN (+ "REPSit Dashboard" one click away, C0820)
 *
 * Routes stay where the code lives today (`/drew/...`); P6-14 moves the
 * code, not these labels. Icons are names resolved by the Sidebar (keeps this module pure).
 */
import { ASSISTANTS } from '../../assistants/assistants.js';

export const DA = '/drew';

/** DREW routes that are PLATFORM pages per D10 (REPSit LEARNING / Settings) — the REPSit sidebar shows there. */
export const PLATFORM_PATHS_UNDER_DA = Object.freeze([`${DA}/case-studies`, `${DA}/documents`, `${DA}/settings`]);

/** Where an active IA lives today (the registry's `route` is the future `/drew` URL; P6-14 aligns them). */
export const ASSISTANT_PATHS = Object.freeze({ drew: DA });

export const SIDEBAR_MODES = Object.freeze({ repsit: 'repsit', drew: 'drew' });

/** Which sidebar a path gets. */
export function sidebarModeFor(pathname) {
  const p = String(pathname || '');
  if (!p.startsWith(DA)) return SIDEBAR_MODES.repsit;
  if (PLATFORM_PATHS_UNDER_DA.some((base) => p === base || p.startsWith(`${base}/`))) return SIDEBAR_MODES.repsit;
  return SIDEBAR_MODES.drew;
}

/* ------------------------------------------------------------------ shared item lists */

// ⚑Q-26 applied provisionally: "Individual Contributor" throughout (TICK A25) — the sidebar item
// no longer uses the retired "ITM" abbreviation. Tim confirms on his side-by-side pass.
export const ROLE_DASHBOARD_ITEMS = Object.freeze([
  { id: 'role-itm', text: 'Individual Contributor Dashboard', icon: 'Engineering', path: `${DA}/role/itm` },
  { id: 'role-fls', text: 'FLS Dashboard', icon: 'SupervisorAccount', path: `${DA}/role/fls` },
  { id: 'role-dh', text: 'DH Dashboard', icon: 'AccountTree', path: `${DA}/role/dh` },
  { id: 'role-executive', text: 'Executive Dashboard', icon: 'BusinessCenter', path: `${DA}/role/executive` },
]);

export const LEARNING_ITEMS = Object.freeze([
  { id: 'case-studies', text: 'Industry Case Studies', icon: 'MenuBook', path: `${DA}/case-studies` },
  { id: 'documents', text: 'Document Upload Portal', icon: 'Description', path: `${DA}/documents` }, // P5-12 (D09 §3 title; D10 sketch said "Internal Documents")
]);

/** The ten IAs from the registry: active ones navigate, the others are greyed ("Coming to REPSit.ai"). */
export function assistantItems(assistants = ASSISTANTS) {
  return [...assistants]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((a) => ({
      id: `ia-${a.id}`,
      text: a.name,
      secondary: a.shortTitle,
      avatar: a.avatarSmall || a.avatar,
      path: a.active ? ASSISTANT_PATHS[a.id] || a.route || DA : null,
      disabled: !a.active,
      tooltip: a.active ? a.shortTitle : `${a.name} — Coming to REPSit.ai`,
    }));
}

/* ------------------------------------------------------------------ REPSit sidebar (D10) */

/** Tim (1 Nov): the API and its documentation are for Org IT and Administrators only. */
export const API_PERSONAS = Object.freeze(['admin', 'ppi', 'org_it']);
export function canSeeApi(personaId) { return !personaId || API_PERSONAS.includes(personaId); }

export function repsitSidebar({ isSuperAdmin = false, assistants, showApi = true } = {}) {
  return [
    {
      key: 'home',
      label: null,
      items: [{ id: 'dashboard', text: 'Dashboard', icon: 'Home', path: '/dashboard' }],
    },
    { key: 'access', label: 'Access Levels', items: [...ROLE_DASHBOARD_ITEMS] },
    { key: 'learning', label: 'REPSit Learning', items: [...LEARNING_ITEMS] },
    { key: 'assistants', label: 'Intelligent Assistants', items: assistantItems(assistants) },
    {
      key: 'admin',
      label: 'Admin',
      items: [
        ...(isSuperAdmin ? [{ id: 'admin', text: 'Admin', icon: 'AdminPanelSettings', path: '/admin' }] : []),
        { id: 'settings', text: 'User Settings', icon: 'Settings', path: '/settings' },
        { id: 'help', text: 'Help & Docs', icon: 'HelpOutline', path: '/help' },
        { id: 'org-it', text: 'Org IT Portal', icon: 'Storage', path: `${DA}/role/org-it` },
        ...(showApi ? [{ id: 'api', text: 'API', icon: 'Code', path: '/api-docs' }] : []),
      ],
    },
  ];
}

/* ------------------------------------------------------------------ DREW sidebar (D10 + Q-09 proposal) */

export function drewSidebar({ featureHugo = false, showApi = true } = {}) {
  return [
    {
      key: 'home',
      label: null,
      items: [{ id: 'oe-dashboard', text: 'OE Dashboard', icon: 'Dashboard', path: DA }],
    },
    // ⚑ D-UI-03 (UI review 2026-09-21): starts collapsed — every tester is an admin, so all four role
    // dashboards showed above the thing they came to do. Still one click away; D10 order unchanged.
    { key: 'access', label: 'Access Levels', defaultClosed: true, items: [...ROLE_DASHBOARD_ITEMS] },
    {
      key: 'input',
      label: 'OE Input / Assessment',
      items: [
        { id: 'trl-entry', text: 'OE Condition Entry Portal', icon: 'AddCircleOutline', path: `${DA}/rollup/trl-entry` },
        { id: 'review-queue', text: 'L1/FLS Review Queue', icon: 'RuleFolder', path: `${DA}/rollup/review-queue` },
        { id: 'severity-calibration', text: 'L2/DH Approval Queue', icon: 'Gavel', path: `${DA}/rollup/severity-calibration` },
        // ⚑Q-09 proposal: Surveys live here as "D&A Perspectives Surveys" (D10 omits them)
        { id: 'surveys', text: 'D&A Perspectives Surveys', icon: 'Assignment', path: `${DA}/surveys` },
      ],
    },
    {
      key: 'review',
      label: 'Data Review / Comparison',
      items: [
        { id: 'health-messages', text: 'OE Health Alerts', icon: 'HealthAndSafety', path: `${DA}/ia/health-messages` },
        { id: 'site', text: 'Site Dashboard', icon: 'LocationCity', path: `${DA}/rollup/site` },
        { id: 'regional', text: 'Regional Rollup', icon: 'Public', path: `${DA}/rollup/regional` },
        { id: 'enterprise', text: 'Enterprise Rollup', icon: 'Language', path: `${DA}/rollup/enterprise` },
        { id: 'comparisons', text: 'Comparisons', icon: 'CompareArrows', path: `${DA}/ia/comparisons` },
        { id: 'reports-on-demand', text: 'Reports on Demand', icon: 'Article', path: `${DA}/ia/reports-on-demand` },
        // ⚑Q-09 proposal: Quick Reports · Report Builder · Warning History stay reachable as one "Reports" item
        { id: 'reports', text: 'Reports', icon: 'Assessment', path: `${DA}/reports` },
        ...(featureHugo ? [{ id: 'barometer', text: 'HP Barometer', icon: 'Speed', path: '/hugo/barometer' }] : []),
      ],
    },
    {
      key: 'main',
      label: 'Main',
      items: [
        { id: 'repsit-dashboard', text: 'REPSit Dashboard', icon: 'Home', path: '/dashboard' },
        { id: 'help', text: 'Help & Docs', icon: 'HelpOutline', path: '/help#drew' }, // P6-16: opens the DREW section
        ...(showApi ? [{ id: 'api-docs', text: 'DREW API Docs', icon: 'Code', path: '/api-docs' }] : []),
      ],
    },
  ];
}

/* ------------------------------------------------------------------ helpers */

/** Keep only items the viewer may open (`canAccess(path)` = accessControl.canAccessToolPath); drop empty sections. */
export function filterSidebar(sections, canAccess) {
  return sections
    .map((s) => ({ ...s, items: s.items.filter((it) => it.disabled || !it.path || canAccess(it.path)) }))
    .filter((s) => s.items.length > 0);
}

/** Section key that contains `pathname` (exact match, or the longest prefix match), or null. */
export function activeSectionFor(sections, pathname) {
  let best = null;
  let bestLen = -1;
  for (const s of sections) {
    for (const it of s.items) {
      if (!it.path) continue;
      const exact = pathname === it.path;
      const prefix = it.path !== DA && it.path !== '/' && pathname.startsWith(`${it.path}/`);
      if ((exact || prefix) && it.path.length > bestLen) {
        best = s.key;
        bestLen = it.path.length;
      }
    }
  }
  return best;
}

/** Is this item the current page? Exact path, or a child route — except the two dashboards roots (exact only). */
export function isItemActive(item, pathname) {
  if (!item.path) return false;
  const base = item.path.split('#')[0]; // '/help#drew' is active on '/help'
  if (pathname === base) return true;
  if (base === DA || base === '/dashboard') return false;
  return pathname.startsWith(`${base}/`);
}
